import AnchorNavItem from './anchor_nav_item';
import classNames from 'classnames';
import { CONDENSED_HEADER, EDITION_BASENAME_EN_CA } from '../../../constants';
import {defer, get, isEmpty} from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import RosettaFormattedMessage from '../rosetta_formatted_message';
import { AngleDown } from '../fa_icons';
import MediaQuery from '../media_query';
import { nodeAttrs } from '../../../utils/dom';

const PX_THRESHOLD_STICKY_HEADER = 4;

const Aux = props => props.children;

const arrayToAnchorNavItem = (values) => values.map(obj => <AnchorNavItem key={obj.key} name={obj.name}/>);

export default class AnchorNavigation extends Component {
  constructor(props, context) {
    super(props, context);
    const basename = get(context, 'edition_data.basename', '');
    this.state = {
      hideDropdown: true,
      isSticky: false,
      raisePosition: Boolean(basename === EDITION_BASENAME_EN_CA)
    };
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
  }

  shouldComponentUpdate(_, nextState) {
    return (
      nextState.hideDropdown !== this.state.hideDropdown ||
      nextState.isSticky !== this.state.isSticky
    );
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }

  toggleHideDropdown = () => {
    this.setState({ hideDropdown: !this.state.hideDropdown });
  }

  handleClick = (event) => {
    event.preventDefault();

    /*
     * If the dropdown is visible when `click` is triggered,
     *
     * - And we're not on a touch device,
     *   then we have to hide the dropdown.
     *
     * - And we are on a touch device,
     *   we should not hide the dropdown. This is because the
     *   `click` event always follows the `mouseEnter` event, which
     *   by this point, has _just_ opened the dropdown. If we then
     *   hide it immediately afterwards, it will look to the user as
     *   though nothing happened.
     */

    if (!this._mouseEnterWasJustTriggered) {
      this.setState({ hideDropdown: !this.state.hideDropdown });
    }
  }

  handleMouseEnter = (event) => {
    event.preventDefault();
    if (!this.state.hideDropdown) {
      /*
       * If the dropdown is visible when `mouseEnter` is triggered,
       *
       * - And we're not on a touch device,
       *   then we don't have to do anything.
       *
       * - And we are on a touch device,
       *   then that means the user tapped the dropdown.
       *   The dropdown must be hidden. However, we don't want that hide
       *   behavior to happen on desktop, so we should not do anything here
       *   and instead have `handleClick` handle things.
       */
      return false;
    }

    /*
     * If the dropdown is not visible when `mouseEnter` is triggered,
     *
     * - And we're not on a touch device,
     *   then we have to show the dropdown, since we expect the dropdown
     *   to show when a user hovers.
     *
     * - And we are on a touch device,
     *   Then that means the user tapped the dropdown. In this case
     *   we also have to show the dropdown. However, the touch device
     *   will fire a `click` event immediately afterwards. To prevent
     *   anything from happening when that click event fires, let's set a
     *   flag that only persists for the duration of the current call stack.
     */
    this.setState({ hideDropdown: false });
    this._mouseEnterWasJustTriggered = true;
    defer(() => {
      this._mouseEnterWasJustTriggered = false;
    });
  }

  handleMouseLeave = () => {
    if (!this.state.hideDropdown) {
      this.setState({ hideDropdown: true });
    }
  }

  handleScroll = () => {
    if (this.state.isSticky !== this.shouldBeSticky()) {
      this.setState({ isSticky: !this.state.isSticky });
    }
  }

  primaryLabel = () => (
    <RosettaFormattedMessage parentFilename={__filename}
      id="primary_label"
      description="The label on the anchor navigation on mobile and tablet"
      defaultMessage="Jump to chapter"
    />
  );

  secondaryLabel = () => (
    <Aux>
      <MediaQuery breakpoint="desktop">
        <RosettaFormattedMessage parentFilename={__filename}
          id="secondary_label"
          description="The label on the anchor navigation on desktop"
          defaultMessage="More..."
        />
      </MediaQuery>
      <MediaQuery breakpoint="notDesktop">
        {this.primaryLabel()}
      </MediaQuery>
    </Aux>
  );

  dropdownCell = (cells) => {
    if (!isEmpty(cells)) {
      const anchorLinkDropdownClass = classNames('anchor-link-dropdown', {
        hidden: this.state.hideDropdown,
        sticky: this.state.isSticky
      });
      return (
        <li onClick={this.handleClick} onMouseEnter={this.handleMouseEnter} className="anchor-link dropdown-label">
          <span>{this.secondaryLabel()}</span>
          <AngleDown/>
          <ul className={anchorLinkDropdownClass}>
            <div className="dropdown-content">
              <span className="dropdown-close" />
              {cells}
            </div>
          </ul>
        </li>
      );
    }
  }

  shouldBeSticky = () => {
    const stickyHeader = document.getElementsByClassName(CONDENSED_HEADER).item(0);
    const el = document.getElementsByClassName('anchor-nav-wrapper').item(0);
    const topPos = nodeAttrs(el).top;
    if (stickyHeader && PX_THRESHOLD_STICKY_HEADER) {
      const relativeThreshold = nodeAttrs(stickyHeader).bottom;
      return topPos <= relativeThreshold && relativeThreshold > PX_THRESHOLD_STICKY_HEADER;
    }
    return topPos < 0;
  }

  render() {
    const { landscape, dropdown } = this.props.navItems;
    const landscapeNavItems = arrayToAnchorNavItem(landscape);
    const dropdownNavItems = arrayToAnchorNavItem(dropdown);
    const anchorNavigationClass = classNames('anchor-nav', {
      custom: this.props.onCustomArticle,
      raised: this.state.raisePosition,
      sticky: this.state.isSticky
    });
    return (
      <div className="anchor-nav-wrapper">
        <div className={anchorNavigationClass}>
          <ul className="anchor-links" onMouseLeave={this.handleMouseLeave}>
            <MediaQuery breakpoint="desktop">
              <li className="landscape-label">
                {this.primaryLabel()}
              </li>
              {landscapeNavItems}
              <span className="landscape-dropdown">
                {this.dropdownCell(dropdownNavItems)}
              </span>
            </MediaQuery>
            <MediaQuery breakpoint="notDesktop">
              {this.dropdownCell(landscapeNavItems.concat(dropdownNavItems))}
            </MediaQuery>
          </ul>
        </div>
      </div>
    );
  }
}

AnchorNavigation.contextTypes = {
  edition_data: PropTypes.object
};

AnchorNavigation.propTypes = {
  onCustomArticle: PropTypes.bool,
  navItems: PropTypes.object.isRequired
};
